import { useCallback, useLayoutEffect, useSyncExternalStore } from 'react'
import { Autosave } from '@/core/lib/autosave'
import { currentAutosaveScope } from '@/core/api/autosaveScope'

const AUTOSAVE_DELAY_MS = 800
type AutosaveState = 'idle' | 'saving' | 'error'
type Daten = Record<string, unknown>
type Save = (key: string, value: Daten) => Promise<void>
type Group = {
  save: Save
  isSigned: (key: string) => boolean
  entries: Map<string, Autosave<Daten, Promise<void>>>
  subscribe: (fn: () => void) => () => void
  changed: () => void
  version: number
  scope: string | null
  notify?: (key: string, error: unknown) => void
}
const groups = new Map<string, Group>()

export function retainedAutosave(identity: string): Record<string, Daten> {
  return Object.fromEntries([...(groups.get(identity)?.entries ?? [])].flatMap(([key, queue]) => {
    const value = queue.recovery()
    return value === undefined ? [] : [[key, value]]
  }))
}

export function useAutosave<K extends string>(save: (key: K, value: Daten) => Promise<void>, identity: string, isSigned: (key: K) => boolean, notify?: (key: string, error: unknown) => void) {
  let group = groups.get(identity)
  if (!group) {
    const listeners = new Set<() => void>()
    const created: Group = {
      save: (key, value) => save(key as K, value), entries: new Map(), version: 0,
      isSigned: (key) => isSigned(key as K),
      scope: currentAutosaveScope(),
      subscribe: (fn) => { listeners.add(fn); return () => { listeners.delete(fn) } },
      changed: () => { created.version += 1; for (const fn of listeners) fn() },
    }
    group = created
    groups.set(identity, group)
  }
  const current = group
  current.save = (key, value) => save(key as K, value)
  current.isSigned = (key) => isSigned(key as K)
  current.notify = notify
  useSyncExternalStore(current.subscribe, () => current.version, () => 0)
  const entry = useCallback((key: K) => {
    let controller = current.entries.get(key)
    if (!controller) {
      controller = new Autosave<Daten, Promise<void>>((value) => current.save(key, value), () => currentAutosaveScope() === current.scope && !current.isSigned(key), (error) => current.notify?.(key, error))
      controller.subscribe(current.changed)
      current.entries.set(key, controller)
    }
    return controller
  }, [current])
  // Signed sheets keep their recovery data, but must not replay it or block writable sheets.
  const flush = useCallback(async (retryFailed = false): Promise<boolean> => {
    const entries = [...current.entries]
    await Promise.all(entries.map(([key, queue]) => current.isSigned(key) ? undefined : queue.flush(retryFailed)?.catch(() => {})))
    return entries.every(([key, queue]) => current.isSigned(key) || queue.recovery() === undefined)
  }, [current])
  const schedule = useCallback((key: K, value: Daten) => {
    if (current.isSigned(key)) return
    for (const [otherKey, queue] of current.entries) {
      if (otherKey !== key && !current.isSigned(otherKey) && queue.snapshot() !== 'error') void queue.flush()?.catch(() => {})
    }
    entry(key).schedule(value, AUTOSAVE_DELAY_MS)
  }, [current, entry])
  const isClean = useCallback((key: K) => current.isSigned(key) || !current.entries.get(key)?.recovery(), [current])
  const recovery = useCallback((key: K) => current.isSigned(key) ? undefined : current.entries.get(key)?.recovery(), [current])
  useLayoutEffect(() => () => {
    void flush()
  }, [flush])
  const queues = [...current.entries].filter(([key]) => !current.isSigned(key)).map(([, queue]) => queue)
  const state: AutosaveState = queues.some((queue) => queue.snapshot() === 'error') ? 'error' : queues.some((queue) => queue.snapshot() === 'saving') ? 'saving' : 'idle'
  const status: AutosaveState | 'saved' = state === 'idle' && queues.some((queue) => queue.snapshot() === 'saved') ? 'saved' : state
  return { status, schedule, flush, isClean, recovery }
}
